import React, { EventHandler } from 'react';
import classNames from 'classnames';
import './styles/index.scss';
import * as dataURIs from '../case-detail/_data-uris';
import { InfoCell } from '../info-cell/info-cell';
import { Card, LoadingSlugs } from '@codametrix/ui-components';
import { propAccessor2 } from '../case-prop-accessor/case-prop-accessor2';
import { propAccessor } from '../case-prop-accessor/case-prop-accessor';
import { PropertyAccessor } from '../case-prop-accessor/propery-accessor';
import { DataTransformers } from './encounter-data.transformers';
import { Conditional, Chevron } from '@codametrix/ui-components';
import { conditionalUtils, AmplifyCore } from '@codametrix/ui-common';
import { CMXIcon } from '@codametrix/ui-components';

const { getCoderNotesLookupParams } = AmplifyCore.coderNotesUtils;
const DataStyles = {
  s: 'cmx-col-3 cmx-col-lg-4 cmx-col-md-4',
  ms: 'cmx-col-4 cmx-col-md-6',
  m: 'cmx-col-8',
  l: 'cmx-col-12'
};

type OpenClaim = {
  fieldName: any;
  fieldIdentifier: any;
};

type PatientEncounterProps = {
  activeItem: CMxAPI.CaseRun;
  handleExpansion?: (expanded: boolean) => void;
  showCoderNotesInfo: (coderNotesParams: CMxCommonApp.CoderNotesParams) => void;
  showProviderInfo?: (providerInfo: CMxCommonApp.ProviderSearchParams) => void;
  propertyConfig?: CMxCommonApp.PatientInformationConfiguration;
  showLoading?: boolean;
  openClaims?: OpenClaim[];
  activations?: CMxCommonApp.UserActivations;
  fieldEditorInfo: CMxAPI.FieldEditorInfo;
  capability?: CMxCommonApp.Capability;
  coderNotesCapability?: CMxCommonApp.Capability;
  workItem?: CMxCommonApp.WorkItem;
};

type PatientEncounterState = {
  expandEncounter: boolean;
};

function CellMaker(
  propAccessor: PropertyAccessor,
  rawCaseRun: CMxCommonApp.ExtendedCaseRun,
  fieldEditorInfo: CMxAPI.FieldEditorInfo,
  capability: CMxCommonApp.Capability,
  showProviderInfo?: (providerInfo: CMxCommonApp.ProviderSearchParams) => void
) {
  const dataTransformer = DataTransformers(propAccessor, rawCaseRun);

  return (def: CMxCommonApp.PatientDataDefinition) => {
    const cellClassName = DataStyles[def.dataSize];
    let value = def.transformer
      ? dataTransformer[def.transformer](def.property)
      : propAccessor(def.property, 'N/A');

    const additionalInfo = def?.additionalInfo?.reduce(
      (acc, info: CMxCommonApp.AdditionalPatientDataDef) => {
        const key = info?.label;
        const value = info.transformer
          ? dataTransformer[info.transformer](info.property)
          : propAccessor(info.property, 'N/A');

        // Ensure both key and value are strings
        acc[key] = value?.toString();
        return acc;
      },
      {} as Record<string, any>
    ); // Initialize an empty object with string key-value types

    const _handleUpdate = (updates: any) => {
      const { caserun_uid, service_line } = rawCaseRun;
      const caseEdit = fieldEditorInfo.caseEdits?.[def.key];
      const initialVal = caseEdit?.initialVal;
      const mappedValues = caseEdit?.mappedValues;
      const caseUpdates = { ...updates };
      if (caseUpdates.hasOwnProperty('patient_type')) {
        const patientTypeValue = caseUpdates['patient_type'];
        const mappedValues =
          fieldEditorInfo.caseEdits['patient_class']?.mappedValues || [];
        const value = mappedValues.find(
          (item) => item.label === patientTypeValue
        )?.value;
        if (value !== undefined) {
          caseUpdates['patient_class'] = value;
        }
      }

      fieldEditorInfo.updateCase({
        caseRunUid: caserun_uid,
        serviceLine:
          service_line == 'Inpatient Professional Coding'
            ? 'ipc'
            : service_line,
        caseUpdates,
        initialVal,
        mappedValues
      });
    };

    return (
      <InfoCell
        key={`${def.key}`}
        className={cellClassName}
        cellKey={def.key}
        label={def.label}
        value={value}
        fieldType={def.fieldType}
        fieldEditorInfo={{
          ...fieldEditorInfo,
          updateCase: (updates) => _handleUpdate(updates)
        }}
        capability={capability}
        showProviderInfo={showProviderInfo}
        hasLink={def?.additionalInfo ? def?.hasLink : false}
        additionalInfo={additionalInfo}
      />
    );
  };
}

const CellPatientMaker = (
  propAccessor: PropertyAccessor,
  rawCaseRun: CMxAPI.CaseRun
) => {
  const dataTransformer = DataTransformers(propAccessor, rawCaseRun);

  return (def: CMxCommonApp.PatientDataDefinition) => {
    const value = def.transformer
      ? dataTransformer[def.transformer](def.property)
      : propAccessor(def.property, 'N/A');

    return (
      <div key={def.key} className="patient-cell">
        {value}
        {def.addHr ? <div className="vertical-divider"></div> : null}
      </div>
    );
  };
};

class PatientEncounter extends React.Component<
  PatientEncounterProps,
  PatientEncounterState
> {
  static displayName = 'PatientEncounter';
  static defaultProps = {
    openClaims: [],
    showLoading: false,
    activations: {},
    handleExpansion: (expanded: boolean) => {},
    showCoderNotesInfo: (coderNotesParams: CMxCommonApp.CoderNotesParams) => {},
    showProviderInfo: (providerInfo: CMxCommonApp.ProviderSearchParams) => {},
    propertyConfig: {
      patient: [],
      summary: [],
      expanded: []
    },
    workItem: {
      owner: '',
      assignee: '',
      aging_days: '',
      codable_days: ''
    }
  };

  constructor(props: PatientEncounterProps) {
    super(props);
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleCoderNotesClick = this.handleCoderNotesClick.bind(this);
    this.handleCoderNotesKeyDown = this.handleCoderNotesKeyDown.bind(this);
    this.state = {
      expandEncounter: true
    };
  }

  handleToggleClick(ev: React.MouseEvent<HTMLAnchorElement>) {
    ev.preventDefault();
    const { expandEncounter } = this.state;
    let { openClaims } = this.props;

    if (expandEncounter) {
      openClaims = [];
    } else {
      openClaims = [
        ...openClaims,
        { fieldIdentifier: 'showToggle', fieldName: 'showToggle' }
      ];
    }

    this.setState({
      expandEncounter: !expandEncounter
    });
    this.props.handleExpansion(!expandEncounter);
  }

  expandEncounter(expandEncounter: boolean) {
    this.setState({
      expandEncounter: expandEncounter
    });
    this.props.handleExpansion(expandEncounter);
  }

  /**
   * Handles the click event on the "Coder Notes" section.
   * Prevents default behavior and triggers the coder notes info display.
   *
   * @param {React.MouseEvent<HTMLDivElement>} ev - The mouse event object.
   * @returns {void}
   */
  handleCoderNotesClick(ev: React.MouseEvent<HTMLDivElement>) {
    const activeItem = this.props.activeItem;
    const lookupParams = getCoderNotesLookupParams(activeItem);
    ev.preventDefault();
    this.props.showCoderNotesInfo({
      ...lookupParams,
      patientMRN: activeItem?.patient_identifier?.number,
      serviceLine: activeItem?.service_line
    });
  }

  /**
   * Handles the keydown event on the "Coder Notes" section.
   * Triggers the coder notes info display when the Enter key is pressed.
   *
   * @param {React.KeyboardEvent<HTMLDivElement>} ev - The keyboard event object.
   * @returns {void}
   */
  handleCoderNotesKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
    const activeItem = this.props.activeItem;
    const lookupParams = getCoderNotesLookupParams(activeItem);
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.props.showCoderNotesInfo({
        ...lookupParams,
        patientMRN: activeItem?.patient_identifier?.number,
        serviceLine: activeItem?.service_line
      });
    }
  }

  render(): React.ReactNode {
    const classnames = ['patient-encounter'];
    const { expandEncounter } = this.state;
    const {
      activations,
      activeItem,
      propertyConfig,
      fieldEditorInfo,
      capability,
      coderNotesCapability,
      workItem
    } = this.props;

    const prop = propAccessor2(
      {
        ...activeItem,
        ...workItem
      },
      activations?.property ?? {}
    );

    const cellMaker = CellMaker(
      prop,
      activeItem,
      fieldEditorInfo,
      capability,
      this.props.showProviderInfo
    );
    const cellPatientMaker = CellPatientMaker(prop, activeItem);

    // all info less last item
    let patientConfig = propertyConfig.patient.slice(
      0,
      propertyConfig.patient.length - 1
    );
    // last item
    const patientLastItem = propertyConfig.patient.slice(-1);

    // add hr less the last one item
    patientConfig = patientConfig.map((patient, index) => {
      return {
        ...patient,
        addHr: index + 1 !== patientConfig.length
      };
    });

    const topRightCell = patientLastItem.map(cellMaker);
    const topCells = patientConfig.map(cellPatientMaker);
    const midCells = propertyConfig.summary.map(cellMaker);
    const bottomCells = propertyConfig.expanded.map(cellMaker);

    const chevronClassNames = [
      'chevron-link',
      'qa-expand-encounter',
      { 'chevron-link-opened': expandEncounter },
      { 'chevron-link-closed': !expandEncounter }
    ];

    const expandEncounterClassNames = [
      'expanded-encounter',
      { 'expanded-encounter-opened': expandEncounter },
      { 'expanded-encounter-closed': !expandEncounter }
    ];

    const showComments = conditionalUtils.capabilityValue(
      coderNotesCapability?.on ?? false
    );

    return (
      <div className={classNames(classnames)}>
        <div
          className="patient-info-container"
          data-testid="patient-info-container">
          <div className="patient-top-info-container">
            <div className="top-patient-info">
              <div className="patient-info-primary sensitive-data cmx-columns">
                <div className="cmx-col-12" data-testid="patient-info-name">
                  {prop('patient_name')}
                </div>
                <div className="patient-info-secondary cmx-columns">{topCells}</div>
              </div>
            </div>

            <div className="bottom-patient-info">
              <div className="cmx-columns">{topRightCell}</div>
            </div>
            <Conditional every={[showComments]}>
              <div
                tabIndex={0}
                onClick={this.handleCoderNotesClick}
                onKeyDown={this.handleCoderNotesKeyDown}
                className="coder-comments-section">
                <CMXIcon
                  svgIconClass="coder-comments"
                  svgIconName="BiomeComments"
                  svgIconSize="24"
                />
              </div>
            </Conditional>
          </div>

          {propertyConfig.summary.length > 0 && (
            <div>
              <div className="cmx-divider first-divider"></div>
              <div className="container">
                <div className="cmx-columns info-block">{midCells}</div>
              </div>
            </div>
          )}

          {/* encounter */}
          <div className={classNames(expandEncounterClassNames)}>
            <div className="container">
              <div className="cmx-columns info-block">{bottomCells}</div>
            </div>
          </div>
        </div>
        <Conditional on={propertyConfig.expanded.length > 0}>
          <div className="toggle-section" data-dd-action-name="~Toggle Section">
            <span className="show-toggle" onClick={this.handleToggleClick}>
              <Chevron
                data-dd-action-name="~Toggle Section"
                onClick={this.handleToggleClick}
                className={classNames(chevronClassNames)}
              />
            </span>
          </div>
        </Conditional>
      </div>
    );
  }
}

export { PatientEncounter };
